import React from 'react';
import Table from '@amzn/meridian/table/table';
import TableCell from '@amzn/meridian/table/table-cell';
import TableRow from '@amzn/meridian/table/table-row';
import Tag from '@amzn/meridian/tag/tag';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader/loader';
import Alert from '@amzn/meridian/alert/alert';
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  TrainingRecord,
  TrainingTableEmptyStateDataTestIds,
  TrainingTableSortColumn,
  ViewContext,
  ViewMode,
} from './types';
import { SortDirection } from '../../../Types/SortDirection';
import {
  formatDate,
  getCcxMetricTranslation,
  getStatusColor,
  getTrainingStatusTranslation,
} from './utils';
import EmptyState from '../../../commons/EmptyState';
import Pagination from '@amzn/meridian/pagination';
import Select, { SelectOption } from '@amzn/meridian/select';
import { trainingResultsPerPage } from '../types/constants';
import { useTrainingPagination } from '../hooks/useTrainingPagination';
import { ExtendedUser } from '../../../Types';

interface TrainingTableProps {
  trainings: TrainingRecord[];
  loading: boolean;
  error?: boolean;
  sortColumn: TrainingTableSortColumn;
  sortDirection: SortDirection;
  onSort: (column: TrainingTableSortColumn, direction: SortDirection) => void;
  viewContext: ViewContext;
  isCSAExperience?: boolean;
  selectedCSA?: ExtendedUser;
  viewMode?: ViewMode;
}

const TrainingTable: React.FC<TrainingTableProps> = ({
  trainings,
  loading,
  error,
  sortColumn,
  sortDirection,
  viewContext,
  onSort,
  isCSAExperience = false,
  selectedCSA,
  viewMode,
}) => {
  const intl = useIntl();

  // Use the optimized training pagination hook
  const {
    paginatedData,
    totalPages,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
  } = useTrainingPagination({
    trainings,
    sortColumn,
    sortDirection,
    selectedCSA,
    viewMode,
  });

  const handleSort = ({ sortColumn: column, sortDirection: direction }) => {
    onSort(column, direction);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
  };

  if (loading) {
    return (
      <Row
        type='outline'
        width='100%'
        alignmentHorizontal='center'
        height='400px'
      >
        <Loader />
      </Row>
    );
  }

  if (error) {
    return (
      <Box height='400px' width='fill' spacingInset='300'>
        <Alert type='error'>
          <FormattedMessage
            id='cs_perfportal_training_experience_error_message'
            defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
          />
        </Alert>
      </Box>
    );
  }

  return (
    <Box width='100%' overflowY='auto' overflowX='hidden'>
      <Table
        headerRows={1}
        showDividers
        spacing='medium'
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        layout='fixed'
        onSort={handleSort}
      >
        <TableRow>
          {!isCSAExperience && (
            <TableCell
              sortColumn={TrainingTableSortColumn.ASSOCIATE_NAME}
              width='140px'
            >
              <FormattedMessage
                id='cs_perfportal_training_experience_table_header_associate'
                defaultMessage='Associate'
              />
            </TableCell>
          )}
          <TableCell
            sortColumn={TrainingTableSortColumn.COURSE_NAME}
            width='300px'
          >
            <FormattedMessage
              id='cs_perfportal_training_experience_table_header_training'
              defaultMessage='Training'
            />
          </TableCell>
          <TableCell
            sortColumn={TrainingTableSortColumn.CCX_ATTRIBUTE}
            width='120px'
          >
            <FormattedMessage
              id='cs_perfportal_training_experience_table_header_ccx'
              defaultMessage='CCX'
            />
          </TableCell>
          <TableCell
            sortColumn={TrainingTableSortColumn.COMPLETION_STATUS}
            width='140px'
          >
            <FormattedMessage
              id='cs_perfportal_training_experience_table_header_completion_status'
              defaultMessage='Status'
            />
          </TableCell>
          {isCSAExperience && (
            <TableCell
              sortColumn={TrainingTableSortColumn.ASSIGNED_DATE}
              width='140px'
            >
              <FormattedMessage
                id='cs_perfportal_training_experience_table_header_assigned_date'
                defaultMessage='Assigned'
              />
            </TableCell>
          )}
          <TableCell
            sortColumn={TrainingTableSortColumn.DUE_DATE}
            width='140px'
          >
            <FormattedMessage
              id='cs_perfportal_training_experience_table_header_due'
              defaultMessage='Due'
            />
          </TableCell>
          <TableCell
            sortColumn={TrainingTableSortColumn.DURATION}
            width='120px'
          >
            <FormattedMessage
              id='cs_perfportal_training_experience_table_header_duration'
              defaultMessage='Duration'
            />
          </TableCell>
        </TableRow>
        {paginatedData && paginatedData.length > 0 ? (
          paginatedData.map(training => (
            <TableRow key={`${training.id}-${training.associateName}`}>
              {!isCSAExperience && (
                <TableCell width='140px'>
                  <Text breakWord={false} truncate={true}>
                    {training.associateName}
                  </Text>
                </TableCell>
              )}
              <TableCell width='300px'>
                <Text breakWord={false} truncate={true}>
                  <Link href={training.courseLink} target='_blank'>
                    {training.courseName}
                  </Link>
                </Text>
              </TableCell>
              <TableCell width='120px'>
                {training.ccxAttributes.map(ccx => {
                  const ccxTranslation = getCcxMetricTranslation(ccx);
                  return (
                    <Text breakWord={false} truncate={true}>
                      <FormattedMessage
                        id={ccxTranslation.id}
                        defaultMessage={ccxTranslation.defaultMessage}
                      />
                    </Text>
                  );
                })}
              </TableCell>
              <TableCell width='140px'>
                <Tag type={getStatusColor(training.completionStatus)}>
                  {(() => {
                    const statusTranslation = getTrainingStatusTranslation(
                      training.completionStatus
                    );

                    return intl.formatMessage({
                      id: `${statusTranslation.id}`,
                      defaultMessage: `${statusTranslation.defaultMessage}`,
                    });
                  })()}
                </Tag>
              </TableCell>
              {isCSAExperience && (
                <TableCell width='140px'>
                  <Text breakWord={false} truncate={true}>
                    {formatDate(training.assignedDate)}
                  </Text>
                </TableCell>
              )}
              <TableCell width='140px'>
                <Text breakWord={false} truncate={true}>
                  {formatDate(training.dueDate)}
                </Text>
              </TableCell>
              <TableCell width='120px'>
                <Text breakWord={false} truncate={true}>
                  <FormattedMessage
                    id='cs_perfportal_training_experience_duration'
                    defaultMessage='{minutes} min'
                    values={{ minutes: training.duration }}
                  />
                </Text>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell columnSpan={6}>
              <EmptyState
                rowWidths={['grid-12']}
                innerColumnSpacing='600 none 600 none'
                data-testid={
                  TrainingTableEmptyStateDataTestIds[viewContext] ??
                  'empty-state'
                }
              >
                <>
                  {viewContext === ViewContext.TM ? (
                    <FormattedMessage
                      id='cs_perfportal_no_associates_trainings'
                      defaultMessage='Your associates have not been assigned any trainings'
                    />
                  ) : (
                    <FormattedMessage
                      id='cs_perfportal_no_trainings'
                      defaultMessage='You have no recent trainings'
                    />
                  )}
                  <br></br>
                  <FormattedMessage
                    id='cs_perfportal_three_months_trainings'
                    defaultMessage='Inspire only shows the last 3 months of training history'
                  />
                </>
              </EmptyState>
            </TableCell>
          </TableRow>
        )}
      </Table>
      <Box width={'100%'} spacingInset='400 none none none'>
        <Row widths='fill'>
          <Row>
            <Text>
              <FormattedMessage
                id='cs_perfportal_arc_pagination_setting'
                defaultMessage='Results per page'
              />
            </Text>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              width={300}
            >
              {trainingResultsPerPage.map(resultsPerPage => {
                return (
                  <SelectOption
                    value={resultsPerPage}
                    label={intl.formatMessage(
                      {
                        id: 'cs_perfportal_arc_pagination_items',
                        defaultMessage: '{itemCount} items',
                      },
                      { itemCount: resultsPerPage }
                    )}
                  />
                );
              })}
            </Select>
          </Row>
          <Row alignmentHorizontal='end'>
            <Pagination
              showSkipArrows
              numberOfPages={totalPages}
              onChange={setCurrentPage}
              currentPage={currentPage}
            />
          </Row>
        </Row>
      </Box>
    </Box>
  );
};

export default React.memo(TrainingTable);
